import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import PrivacyPolicy from './PrivacyPolicy';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_KEY
);

// ── Auth Modal ────────────────────────────────────────────────────────────────
function AuthModal({ onClose, onAuth }) {
  const [mode, setMode]       = useState('login'); // 'login' | 'signup'
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      let result;
      if (mode === 'signup') {
        result = await supabase.auth.signUp({ email, password });
        if (result.error) throw result.error;
        if (result.data?.user && !result.data.session) {
          setError('Check your email to confirm your account, then sign in.');
          setLoading(false); return;
        }
      } else {
        result = await supabase.auth.signInWithPassword({ email, password });
        if (result.error) throw result.error;
      }
      onAuth(result.data.user);
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={sa.overlay} onClick={onClose}>
      <div style={sa.modal} onClick={e => e.stopPropagation()}>
        <button style={sa.close} onClick={onClose}>✕</button>
        <div style={sa.title}>{mode === 'login' ? 'Sign in' : 'Create account'}</div>
        <div style={sa.subtitle}>
          {mode === 'login' ? 'Welcome back to Snow Logik' : 'Join Snow Logik — free forever, upgrade anytime'}
        </div>
        <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
          <input
            style={sa.input}
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required autoFocus
          />
          <input
            style={sa.input}
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required minLength={6}
          />
          {error && <div style={sa.error}>{error}</div>}
          <button style={sa.btn} type="submit" disabled={loading}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>
        <div style={sa.toggle}>
          {mode === 'login' ? (
            <>No account? <button style={sa.link} onClick={() => { setMode('signup'); setError(''); }}>Sign up free</button></>
          ) : (
            <>Already have one? <button style={sa.link} onClick={() => { setMode('login'); setError(''); }}>Sign in</button></>
          )}
        </div>
      </div>
    </div>
  );
}

const sa = {
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400 },
  modal:    { background: '#fff', borderRadius: 14, padding: '28px 24px', maxWidth: 380, width: '90%', position: 'relative' },
  close:    { position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#94a3b8', cursor: 'pointer' },
  title:    { fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#64748b' },
  input:    { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 10, outline: 'none', boxSizing: 'border-box', display: 'block' },
  error:    { fontSize: 13, color: '#dc2626', background: '#fef2f2', borderRadius: 6, padding: '8px 10px', marginBottom: 10 },
  btn:      { width: '100%', padding: '11px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 4 },
  toggle:   { marginTop: 16, textAlign: 'center', fontSize: 13, color: '#64748b' },
  link:     { background: 'none', border: 'none', color: '#185FA5', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
};

// ── Pricing Modal ─────────────────────────────────────────────────────────────
function PricingModal({ onClose, user }) {
  const [loading, setLoading] = useState(null); // which plan is loading
  const [error, setError]     = useState('');

  const plans = [
    {
      id: 'monthly',
      label: 'Monthly',
      price: '$1.99',
      period: '/month',
      priceId: process.env.REACT_APP_STRIPE_PRICE_MONTHLY,
      description: 'Cancel anytime — ski bum safe',
      badge: null,
    },
    {
      id: 'seasonal',
      label: 'Season Pass',
      price: '$9.99',
      period: '/season',
      priceId: process.env.REACT_APP_STRIPE_PRICE_SEASONAL,
      description: 'One-time payment, Nov – Apr',
      badge: 'Best Value',
    },
  ];

  const handleCheckout = async (plan) => {
    if (!user) {
      onClose();
      return;
    }
    setLoading(plan.id);
    setError('');
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: plan.priceId,
          userId: user.id,
          userEmail: user.email,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(null);
    }
  };

  const proFeatures = [
    'Modeled SWE ratio (live temp model)',
    'SNOTEL ground-truth data overlay',
    'Compare up to 5 saved spots',
    'Unlimited saved places',
  ];

  return (
    <div style={sp.overlay} onClick={onClose}>
      <div style={sp.modal} onClick={e => e.stopPropagation()}>
        <button style={sp.close} onClick={onClose}>✕</button>
        <div style={sp.title}>Upgrade to Snow Logik Pro</div>
        <div style={sp.subtitle}>Everything you need to know the snow, before you go.</div>

        <div style={sp.features}>
          {proFeatures.map(f => (
            <div key={f} style={sp.feature}>
              <span style={sp.check}>✓</span>
              <span>{f}</span>
            </div>
          ))}
        </div>

        <div style={sp.plans}>
          {plans.map(plan => (
            <div key={plan.id} style={{ ...sp.plan, ...(plan.badge === 'Best Value' ? sp.planHighlight : {}) }}>
              {plan.badge && <div style={sp.badge}>{plan.badge}</div>}
              <div style={sp.planLabel}>{plan.label}</div>
              <div style={sp.planPrice}>
                <span style={sp.planAmount}>{plan.price}</span>
                <span style={sp.planPeriod}>{plan.period}</span>
              </div>
              <div style={sp.planDesc}>{plan.description}</div>
              <button
                style={{ ...sp.planBtn, ...(plan.badge === 'Best Value' ? sp.planBtnHighlight : {}) }}
                onClick={() => handleCheckout(plan)}
                disabled={!!loading}
              >
                {loading === plan.id ? 'Redirecting...' : 'Get Pro'}
              </button>
            </div>
          ))}
        </div>

        {error && <div style={sp.error}>{error}</div>}
        {!user && <div style={{ ...sp.fine, color: '#185FA5', marginBottom: 4 }}>You'll be asked to sign in or create an account at checkout.</div>}
        <div style={sp.fine}>Secure checkout via Stripe. Cancel anytime.</div>
      </div>
    </div>
  );
}

const sp = {
  overlay:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400, padding: '16px' },
  modal:         { background: '#fff', borderRadius: 16, padding: '28px 24px', maxWidth: 520, width: '100%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' },
  close:         { position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#94a3b8', cursor: 'pointer' },
  title:         { fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 },
  subtitle:      { fontSize: 14, color: '#64748b', marginBottom: 16 },
  features:      { background: '#f8fafc', borderRadius: 10, padding: '12px 16px', marginBottom: 20 },
  feature:       { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', padding: '4px 0' },
  check:         { color: '#185FA5', fontWeight: 700, fontSize: 14 },
  plans:         { display: 'flex', gap: 10, marginBottom: 16 },
  plan:          { flex: 1, border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 10px', textAlign: 'center', position: 'relative' },
  planHighlight: { border: '2px solid #185FA5', background: '#f0f7ff' },
  badge:         { position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: '#185FA5', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' },
  planLabel:     { fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
  planAmount:    { fontSize: 24, fontWeight: 700, color: '#0f172a' },
  planPeriod:    { fontSize: 12, color: '#94a3b8' },
  planPrice:     { marginBottom: 4 },
  planDesc:      { fontSize: 11, color: '#94a3b8', marginBottom: 12, minHeight: 28 },
  planBtn:       { width: '100%', padding: '8px 0', background: '#f1f5f9', color: '#185FA5', border: '1px solid #185FA5', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  planBtnHighlight: { background: '#185FA5', color: '#fff', border: 'none' },
  error:         { fontSize: 13, color: '#dc2626', textAlign: 'center', marginBottom: 8 },
  fine:          { fontSize: 11, color: '#94a3b8', textAlign: 'center' },
};

const MAPBOX_TOKEN = 'pk.eyJ1Ijoic25vd2xvZ2lrIiwiYSI6ImNtdGQ2eW8yczAzN2MyeHB0cnJjbWE2N2wifQ.02YXmtR14vUJ_vbclGRoAQ';

const RESORTS = [
  { name: 'Vail',          lat: 39.6433, lon: -106.3781, type: 'resort' },
  { name: 'Telluride',     lat: 37.9375, lon: -107.8123, type: 'resort' },
  { name: 'Aspen',         lat: 39.1911, lon: -106.8175, type: 'resort' },
  { name: 'Wolf Creek',    lat: 37.4667, lon: -106.7833, type: 'resort' },
  { name: 'Taos',          lat: 36.5952, lon: -105.4469, type: 'resort' },
  { name: 'Steamboat',     lat: 40.4572, lon: -106.8045, type: 'resort' },
];

const PASSES = [
  { name: 'Monarch Pass',     lat: 38.5197, lon: -106.3317, type: 'pass' },
  { name: 'Vail Pass',        lat: 39.5422, lon: -106.2156, type: 'pass' },
  { name: 'Red Mtn Pass',     lat: 37.8883, lon: -107.7142, type: 'pass' },
  { name: 'Wolf Creek Pass',  lat: 37.4786, lon: -106.8311, type: 'pass' },
  { name: 'Loveland Pass',    lat: 39.6658, lon: -105.8772, type: 'pass' },
];

// Region-specific resort/pass chip sets so the app leads with locations that
// actually matter to whoever's using it, instead of always showing Colorado
// to a PNW or Northeast skier. 'colorado' reuses the original lists above
// rather than duplicating them.
const REGION_SETS = {
  colorado: {
    label: 'Colorado & Southwest',
    resorts: RESORTS,
    passes: PASSES,
  },
  utah: {
    label: 'Utah',
    resorts: [
      { name: 'Park City',  lat: 40.6514, lon: -111.5080, type: 'resort' },
      { name: 'Alta',       lat: 40.5883, lon: -111.6373, type: 'resort' },
      { name: 'Snowbird',   lat: 40.5820, lon: -111.6558, type: 'resort' },
      { name: 'Brighton',   lat: 40.5977, lon: -111.5836, type: 'resort' },
      { name: 'Solitude',   lat: 40.6199, lon: -111.5924, type: 'resort' },
    ],
    passes: [
      { name: 'Guardsman Pass', lat: 40.6142, lon: -111.5794, type: 'pass' },
      { name: 'Parleys Summit', lat: 40.7211, lon: -111.6297, type: 'pass' },
    ],
  },
  pnw: {
    label: 'Pacific Northwest',
    resorts: [
      { name: 'Crystal Mountain',   lat: 46.9354, lon: -121.4744, type: 'resort' },
      { name: 'Stevens Pass',       lat: 47.7448, lon: -121.0890, type: 'resort' },
      { name: 'Mt. Baker',          lat: 48.8570, lon: -121.6720, type: 'resort' },
      { name: 'Mt. Hood Meadows',   lat: 45.3311, lon: -121.6656, type: 'resort' },
      { name: 'Whistler Blackcomb', lat: 50.1163, lon: -122.9574, type: 'resort' },
    ],
    passes: [
      { name: 'Snoqualmie Pass', lat: 47.4256, lon: -121.4128, type: 'pass' },
      { name: 'White Pass',      lat: 46.6364, lon: -121.3900, type: 'pass' },
    ],
  },
  tahoe: {
    label: 'California & Tahoe',
    resorts: [
      { name: 'Palisades Tahoe',  lat: 39.1969, lon: -120.2357, type: 'resort' },
      { name: 'Mammoth Mountain', lat: 37.6308, lon: -119.0326, type: 'resort' },
      { name: 'Kirkwood',         lat: 38.6850, lon: -120.0654, type: 'resort' },
      { name: 'Heavenly',         lat: 38.9353, lon: -119.9400, type: 'resort' },
    ],
    passes: [
      { name: 'Donner Summit', lat: 39.3183, lon: -120.3358, type: 'pass' },
      { name: 'Ebbetts Pass',  lat: 38.5497, lon: -119.8081, type: 'pass' },
    ],
  },
  northeast: {
    label: 'Northeast',
    resorts: [
      { name: 'Killington', lat: 43.6045, lon: -72.8201, type: 'resort' },
      { name: 'Stowe',      lat: 44.5303, lon: -72.7815, type: 'resort' },
      { name: 'Sugarbush',  lat: 44.1359, lon: -72.8992, type: 'resort' },
      { name: 'Jay Peak',   lat: 44.9235, lon: -72.5136, type: 'resort' },
    ],
    passes: [
      { name: 'Smugglers Notch', lat: 44.5761, lon: -72.7967, type: 'pass' },
    ],
  },
};

// Simple lat/lon bounding-box classifier — good enough for "which region's
// chips should this person see," not meant to be a precise state lookup.
function classifyRegion(lat, lon) {
  if (lat >= 42 && lon <= -116) return 'pnw';
  if (lat >= 37 && lat < 42 && lon <= -119) return 'tahoe';
  if (lat >= 37 && lat < 42 && lon > -114 && lon <= -109) return 'utah';
  if (lat >= 40 && lon >= -80 && lon <= -66) return 'northeast';
  return 'colorado';
}

// Translates a snow density ratio into a plain-English "snow type" label for
// the badge shown with the forecast. Deliberately describes the forecasted
// NEW snow only — never claims to know actual on-mountain surface
// conditions (packed, icy, groomed, etc.), since the app has no way to
// verify that and shouldn't pretend otherwise.
function snowTypeLabel(r) {
  if (r <= 7)  return { label: 'Heavy & Wet',      color: '#0369A1' };
  if (r <= 11) return { label: 'Average Density',  color: '#185FA5' };
  if (r <= 15) return { label: 'Light & Dry',      color: '#7C3AED' };
  return { label: 'Ultra Light', color: '#9333EA' };
}

const RATIOS = [
  { value: 6,  label: '6:1',  desc: 'Heavy wet', symbol: '❄' },
  { value: 10, label: '10:1', desc: 'Average',    symbol: '✳' },
  { value: 12, label: '12:1', desc: 'Dry powder', symbol: '❅' },
  { value: 18, label: '18:1', desc: 'Champagne',  symbol: '✦' },
];

// Snow-to-liquid ratio estimate from surface air temperature (°F).
// Approximates the general shape of published SLR-vs-temperature curves
// (e.g. Judson & Doesken): wettest near freezing, peaking in the ~10-20°F
// "dry powder" zone, then tapering again in very cold, low-moisture air.
// This is a surface-temp-only estimate, not a substitute for a real
// atmospheric sounding, so treat it as a modeled guess, not gospel.
function tempToRatio(tempF) {
  const t = parseFloat(tempF);
  if (Number.isNaN(t)) return 10;
  if (t >= 34) return 5;
  if (t >= 30) return 8 + (34 - t) * 0.5;
  if (t >= 20) return 10 + (30 - t) * 0.6;
  if (t >= 10) return 16 + (20 - t) * 0.6;
  if (t >= 0)  return 22 - (10 - t) * 0.5;
  return Math.max(12, 17 - (0 - t) * 0.3);
}

// Precip-weighted average modeled ratio across an hourly window, so hours
// with more moisture falling count for more than dry, cold hours with
// nothing coming down.
function computeModeledRatio(precipArr, tempArr) {
  let weightedSum = 0, totalPrecip = 0;
  for (let i = 0; i < precipArr.length; i++) {
    const p = parseFloat(precipArr[i]) || 0;
    if (p <= 0) continue;
    weightedSum += tempToRatio(tempArr[i]) * p;
    totalPrecip += p;
  }
  return totalPrecip > 0 ? weightedSum / totalPrecip : 10;
}

const PERIODS = [
  { key: '24hr', label: '24 hr',  hours: 24  },
  { key: '48hr', label: '48 hr',  hours: 48  },
  { key: '1wk',  label: '1 week', hours: 168 },
  { key: '2wks', label: '2 wks',  hours: 336 },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function MapView({ location, onLocationChange }) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.onload = () => {
      window.mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new window.mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/outdoors-v12',
        center: [location.lon, location.lat],
        zoom: 10,
      });

      map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
      map.addControl(new window.mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
        showUserHeading: false,
      }), 'top-right');

      const el = document.createElement('div');
      el.style.cssText = `
        width:36px;height:36px;background:#185FA5;border:3px solid #fff;
        border-radius:50% 50% 50% 0;transform:rotate(-45deg);
        box-shadow:0 2px 8px rgba(0,0,0,0.3);cursor:pointer;
      `;

      const marker = new window.mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([location.lon, location.lat])
        .addTo(map);

      markerRef.current = marker;
      mapRef.current = map;

      map.on('click', (e) => {
        const { lng, lat } = e.lngLat;
        marker.setLngLat([lng, lat]);
        fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&types=place,region,poi`)
          .then(r => r.json())
          .then(data => {
            const name = data.features?.[0]?.text || `${lat.toFixed(2)}°N`;
            onLocationChange({ name, lat, lon: lng });
          });
      });
    };
    document.head.appendChild(script);

    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    markerRef.current.setLngLat([location.lon, location.lat]);
    mapRef.current.flyTo({ center: [location.lon, location.lat], zoom: 10, duration: 1200 });
  }, [location]);

  return (
    <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', height: 260 }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      <div style={{
        position: 'absolute', bottom: 10, left: 10,
        background: 'rgba(255,255,255,0.92)', borderRadius: 8,
        padding: '6px 10px', fontSize: 12, color: '#0f172a',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)', pointerEvents: 'none',
      }}>
        📍 {location.name} · {location.lat.toFixed(2)}°N {Math.abs(location.lon).toFixed(2)}°W
      </div>
      <div style={{
        position: 'absolute', top: 10, left: 10,
        background: 'rgba(24,95,165,0.9)', borderRadius: 6,
        padding: '4px 8px', fontSize: 11, color: '#fff', pointerEvents: 'none',
      }}>
        Tap anywhere for SWE forecast
      </div>
    </div>
  );
}


function HourlyChart({ hourly, hours, ratio, toDisplay, u, imperial }) {
  const precips = hourly.precipitation.slice(0, hours).map(v => parseFloat(v) || 0);
  const temps   = hourly.temperature_2m.slice(0, hours).map(v => parseFloat(v) || 0);
  const times   = hourly.time.slice(0, hours);
  const isModeled = ratio === 'modeled';
  const hourRatios = isModeled ? temps.map(t => tempToRatio(t)) : temps.map(() => ratio);
  const snows   = precips.map((p, i) => toDisplay(p * hourRatios[i]));
  const maxSnow = Math.max(...snows, 0.01);

  const barW  = hours === 24 ? 16 : 9;
  const gap   = hours === 24 ? 4  : 2;
  const width = hours * (barW + gap);

  const labelEvery = hours === 24 ? 3 : 6;

  const tempY = (i) => {
    const temp = imperial ? temps[i] : (temps[i] - 32) * 5 / 9;
    return 120 - ((temp - (imperial ? -20 : -29)) / (imperial ? 90 : 50)) * 28;
  };
  const tempLinePoints = temps.map((_, i) => `${i * (barW + gap) + barW / 2},${tempY(i)}`).join(' ');

  return (
    <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: 4 }}>
      <svg width={width} height={160} style={{ display: 'block', minWidth: '100%' }}>
        {snows.map((snow, i) => {
          const barH   = Math.max((snow / maxSnow) * 100, snow > 0 ? 3 : 0);
          const x      = i * (barW + gap);
          const y      = 110 - barH;
          const isCold = temps[i] <= 32;
          const color  = snow > 0 ? (isModeled ? '#7C3AED' : (isCold ? '#185FA5' : '#64B4F0')) : '#e2e8f0';

          const dt     = new Date(times[i]);
          const hr     = dt.getHours();
          const showLabel = hr % labelEvery === 0;
          const label  = hr === 0 ? dt.toLocaleDateString('en-US',{weekday:'short'})
                       : hr === 12 ? '12p'
                       : hr < 12  ? `${hr}a`
                       : `${hr-12}p`;

          return (
            <g key={i}>
              {/* bar */}
              <rect
                x={x} y={y} width={barW} height={barH}
                rx={2} fill={color}
              />
              {/* snow amount label on tall bars */}
              {snow >= maxSnow * 0.25 && snow > 0 && (
                <text
                  x={x + barW / 2} y={y - 3}
                  textAnchor="middle" fontSize={8} fill={isModeled ? '#7C3AED' : '#185FA5'} fontFamily="monospace"
                >
                  {snow.toFixed(1)}
                </text>
              )}
              {/* hour label */}
              {showLabel && (
                <text
                  x={x + barW / 2} y={148}
                  textAnchor="middle" fontSize={8.5} fill="#94a3b8" fontFamily="sans-serif"
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}
        {/* baseline */}
        <line x1={0} y1={111} x2={width} y2={111} stroke="#e2e8f0" strokeWidth={1}/>
        {/* temp baseline at freezing */}
        <line x1={0} y1={112} x2={width} y2={112} stroke="#dbeafe" strokeWidth={0.5} strokeDasharray="3,3"/>
        {/* temperature trend line */}
        <polyline points={tempLinePoints} fill="none" stroke="#F97316" strokeWidth={1.5} opacity={0.85} />
        {temps.map((t, i) => (
          <circle
            key={i}
            cx={i * (barW + gap) + barW / 2}
            cy={tempY(i)}
            r={2.5}
            fill={temps[i] <= 32 ? '#185FA5' : '#F97316'}
          />
        ))}
      </svg>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: isModeled ? '#7C3AED' : '#185FA5', display: 'inline-block' }}/>
          Snowfall ({u}){isModeled ? ' · modeled ratio' : ''}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 14, height: 2, background: '#F97316', display: 'inline-block' }}/>
          Temp trend
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#185FA5', display: 'inline-block' }}/>
          Snow likely (&lt;32°F)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#F97316', display: 'inline-block' }}/>
          Rain likely (&gt;32°F)
        </span>
      </div>
    </div>
  );
}

// ── AD SLOTS ────────────────────────────────────────────────────────────
// To activate a real ad in either slot below: drop the image file in
// /public/ads/, then fill in that slot's object here with the image path,
// destination link, alt text, and advertiser name. Leave a slot as `null`
// to keep showing the placeholder. No other code changes needed — both
// slots render themselves automatically based on this config.
//
// Example once you have a real deal:
//   sidebar: {
//     image: '/ads/gore-tex-300x250.jpg',
//     link: 'https://gore-tex.com',
//     alt: 'GORE-TEX — Built for the mountain',
//     advertiser: 'GORE-TEX',
//   }
const AD_CONFIG = {
  sidebar: null, // 300 × 250
  banner:  null, // 728 × 90 desktop / 320 × 50 mobile
};

// Flip this to true ONLY after actually enabling the 1% pledge in the
// Stripe Dashboard (Dashboard → Climate Commitments). This is a real
// claim shown to users, not decorative copy — don't turn it on early.
//
// Per Stripe's own badge usage terms: if you ever pause or turn off the
// Climate program, flip this back to false immediately. Using the badge
// while the program is inactive is a violation of their usage terms, not
// just bad practice.
const CLIMATE_PLEDGE_ENABLED = true;

function AdSlot({ slot, sizeLabel }) {
  const ad = AD_CONFIG[slot];
  if (!ad) {
    return (
      <div style={s.adSlot}>
        <div style={s.adLabel}>Advertisement</div>
        <p style={s.adText}>{sizeLabel}</p>
      </div>
    );
  }
  return (
    <a
      href={ad.link}
      target="_blank"
      rel="noopener noreferrer sponsored"
      style={s.adSlotFilled}
      title={ad.advertiser}
    >
      <img src={ad.image} alt={ad.alt} style={s.adImage} />
    </a>
  );
}

function App() {
  const isMobile = useIsMobile();
  const [location, setLocation]       = useState(RESORTS[0]);
  const [chipRegion, setChipRegion]   = useState('colorado');
  const [weather, setWeather]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [ratio, setRatio]             = useState(10);
  const [period, setPeriod]           = useState('24hr');
  const [imperial, setImperial]       = useState(true);
  const [search, setSearch]           = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [favorites, setFavorites]     = useState([]);
  const [favLoading, setFavLoading]   = useState(false);

  // Quietly try to detect the user's home region so the resort/pass chips
  // lead with places relevant to them (PNW, Utah, Tahoe, Northeast) instead
  // of always defaulting to Colorado. No prompt, no error UI if this fails
  // or is denied — it just silently keeps the Colorado default.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const region = classifyRegion(pos.coords.latitude, pos.coords.longitude);
        setChipRegion(region);
        // Only auto-jump the pinned location if the user hasn't already
        // moved off the default starting resort — don't yank the map out
        // from under someone who's already looking at something.
        setLocation(prev =>
          prev.name === RESORTS[0].name && region !== 'colorado'
            ? REGION_SETS[region].resorts[0]
            : prev
        );
      },
      () => { /* denied or unavailable — stay on the Colorado default */ },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, []);
  const [user, setUser]                   = useState(null);
  const [showAuth, setShowAuth]           = useState(false);
  const [showUpgradeNudge, setShowUpgradeNudge] = useState(false);
  const [showPricing, setShowPricing]     = useState(false);

  const checkProStatus = useCallback(async (userId) => {
    if (!userId) { setIsPro(false); return; }
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, current_period_end')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();
      if (error || !data) { setIsPro(false); return; }
      // Check subscription hasn't expired
      const isActive = data.status === 'active' &&
        (!data.current_period_end || new Date(data.current_period_end) > new Date());
      setIsPro(isActive);
    } catch {
      setIsPro(false);
    }
  }, []);

  // Fetch saved places from Supabase
  const fetchFavorites = useCallback(async (userId) => {
    if (!userId) { setFavorites([]); return; }
    setFavLoading(true);
    try {
      const { data, error } = await supabase
        .from('saved_places')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setFavorites(data || []);
    } catch (err) {
      console.error('Error fetching saved places:', err);
    } finally {
      setFavLoading(false);
    }
  }, []);

  // Session management
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      fetchFavorites(u?.id);
      checkProStatus(u?.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      fetchFavorites(u?.id);
      checkProStatus(u?.id);
    });
    return () => subscription.unsubscribe();
  }, [fetchFavorites, checkProStatus]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setFavorites([]);
    setIsPro(false);
  };

  const [isPro, setIsPro] = useState(false);

  // Check Pro subscription status
  // Handle post-checkout redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      setShowPricing(false);
      window.history.replaceState({}, '', '/');
      setTimeout(() => checkProStatus(user?.id), 3000);
      alert('Welcome to Snow Logik Pro! Your account will be upgraded shortly.');
    }
    if (params.get('upgrade') === 'cancelled') {
      window.history.replaceState({}, '', '/');
    }
  }, [checkProStatus, user]);

  const fetchWeather = useCallback(async (lat, lon) => {
    setLoading(true); setError(null);
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&hourly=precipitation,temperature_2m&daily=precipitation_sum,temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&precipitation_unit=inch&timezone=auto&forecast_days=14`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('fetch failed');
      setWeather(await res.json());
    } catch { setError('Could not load forecast. Please try again.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWeather(location.lat, location.lon); }, [location, fetchWeather]);

  // SNOTEL ground-truth lookup (Pro only). Refetches whenever the pinned
  // location or Pro status changes.
  const [snotel, setSnotel] = useState(null);
  const [snotelLoading, setSnotelLoading] = useState(false);
  useEffect(() => {
    if (!isPro) { setSnotel(null); return; }
    let cancelled = false;
    setSnotelLoading(true);
    fetch(`/api/snotel-nearest?lat=${location.lat}&lon=${location.lon}`)
      .then(res => res.json())
      .then(data => { if (!cancelled) setSnotel(data); })
      .catch(() => { if (!cancelled) setSnotel(null); })
      .finally(() => { if (!cancelled) setSnotelLoading(false); });
    return () => { cancelled = true; };
  }, [location, isPro]);

  const toDisplay = (inches) => imperial ? inches : inches * 2.54;
  const u    = imperial ? 'in' : 'cm';

  // Resort comparison (Pro only). Entirely on-demand — nothing here fetches
  // until the user picks locations, picks a ratio, and taps Compare.
  const MAX_COMPARE = 5;
  const [showCompare, setShowCompare] = useState(false);
  const [compareSelected, setCompareSelected] = useState([]);
  const [compareRatio, setCompareRatio] = useState(10);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareResults, setCompareResults] = useState(null);

  const toggleCompareSelect = (id) => {
    setCompareSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, id];
    });
  };

  const runCompare = async () => {
    if (compareSelected.length < 2) return;
    setCompareLoading(true);
    setCompareResults(null);
    try {
      const selectedFavs = favorites.filter(f => compareSelected.includes(f.id));
      const results = await Promise.all(selectedFavs.map(async (f) => {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${f.lat}&longitude=${f.lon}&hourly=precipitation,temperature_2m&temperature_unit=fahrenheit&precipitation_unit=inch&timezone=auto&forecast_days=14`;
        const res = await fetch(url);
        const data = await res.json();
        const fullPrecip = data.hourly?.precipitation || [];
        const fullTemp   = data.hourly?.temperature_2m || [];

        // One fetch per location, sliced four ways — no need to hit the
        // API again per timeframe, so this stays a single request each.
        const byPeriod = {};
        PERIODS.forEach(p => {
          const precipArr = fullPrecip.slice(0, p.hours);
          const tempArr   = fullTemp.slice(0, p.hours);
          const precip = precipArr.reduce((s, v) => s + (parseFloat(v) || 0), 0);
          const effRatio = compareRatio === 'modeled' ? computeModeledRatio(precipArr, tempArr) : compareRatio;
          byPeriod[p.key] = { snow: toDisplay(precip * effRatio), ratio: effRatio };
        });

        return {
          id: f.id,
          name: f.name,
          elevation: data.elevation,
          byPeriod,
        };
      }));
      // Rank by the 2-week total — the fullest picture of a storm cycle.
      results.sort((a, b) => b.byPeriod['2wks'].snow - a.byPeriod['2wks'].snow);
      setCompareResults(results);
    } catch (err) {
      console.error('Compare error:', err);
      setCompareResults([]);
    } finally {
      setCompareLoading(false);
    }
  };
  const fmt  = (n) => Number(n).toFixed(1);
  const fmt2 = (n) => Number(n).toFixed(2);

  const getPeriodData = () => {
    if (!weather?.hourly) return { precip: 0, temp: 32, modeledRatio: 10 };
    const hrs   = Math.min(PERIODS.find(p => p.key === period)?.hours || 24, weather.hourly.precipitation.length);
    const precipArr = weather.hourly.precipitation.slice(0, hrs);
    const tempArr   = weather.hourly.temperature_2m.slice(0, hrs);
    const precip = precipArr.reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const temp   = tempArr.reduce((s, v) => s + (parseFloat(v) || 0), 0) / tempArr.length;
    const modeledRatio = computeModeledRatio(precipArr, tempArr);
    return { precip, temp, modeledRatio };
  };

  const { precip, modeledRatio } = getPeriodData();
  const effectiveRatio = ratio === 'modeled' ? modeledRatio : ratio;
  const snow       = precip * effectiveRatio;
  const dispSnow   = toDisplay(snow);
  const dispPrecip = toDisplay(precip);

  // Dynamic tab title — shows the live number instead of a static app name,
  // useful for anyone with the tab open in the background during a storm.
  useEffect(() => {
    if (loading || error) return;
    document.title = `${fmt(dispSnow)}${u} · ${location.name} — Snow Logik`;
    return () => { document.title = 'Snow Logik — Know the snow, before you go.'; };
  }, [dispSnow, u, location.name, loading, error]);

  const [shareCopied, setShareCopied] = useState(false);
  const handleShare = async () => {
    const text = `${fmt(dispSnow)}${u} projected at ${location.name} — Snow Logik`;
    const shareData = { title: 'Snow Logik', text, url: 'https://snowlogik.com' };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled — ignore */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${text} — https://snowlogik.com`);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch { /* clipboard unavailable — silently do nothing */ }
    }
  };

  const handleSearch = async () => {
    if (!search.trim()) return;
    try {
      const res  = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(search)}&format=json&limit=1`);
      const data = await res.json();
      if (data?.length) {
        const r    = data[0];
        const name = r.display_name.split(',').slice(0, 2).join(',').trim();
        setLocation({ name, lat: parseFloat(r.lat), lon: parseFloat(r.lon) });
        setSearch('');
      }
    } catch {}
  };

  const FREE_PLACE_LIMIT = 3;

  const saveFavorite = async () => {
    if (favorites.find(f => Math.abs(f.lat - location.lat) < 0.01)) return;
    if (!user) { setShowAuth(true); return; }
    if (!isPro && favorites.length >= FREE_PLACE_LIMIT) {
      setShowUpgradeNudge(true); return;
    }
    try {
      const { data, error } = await supabase
        .from('saved_places')
        .insert([{ user_id: user.id, name: location.name, lat: location.lat, lon: location.lon }])
        .select()
        .single();
      if (error) throw error;
      setFavorites(prev => [...prev, data]);
    } catch (err) {
      console.error('Error saving place:', err);
    }
  };

  const removeFavorite = async (id) => {
    try {
      const { error } = await supabase
        .from('saved_places')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
      setFavorites(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      console.error('Error removing place:', err);
    }
  };

  const maxDailyPrecip = weather?.daily
    ? Math.max(...weather.daily.precipitation_sum.map(v => parseFloat(v) || 0), 0.01)
    : 0.01;

  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const Sidebar = () => (
    <div style={isMobile ? s.sidebarMobile : s.sidebar}>
      {isMobile && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Menu</span>
          <button onClick={() => setShowSidebar(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
        </div>
      )}
      <div style={s.sideCard}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={s.sectionLabel}>Saved locations</div>
          {user && !isPro && (
            <span style={{ fontSize: 11, color: favorites.length >= FREE_PLACE_LIMIT ? '#dc2626' : '#94a3b8' }}>
              {favorites.length}/{FREE_PLACE_LIMIT}
            </span>
          )}
        </div>
        {!user ? (
          <div>
            <p style={s.mutedText}>Sign in to save locations across devices.</p>
            <button style={{ ...s.saveBtn, marginTop: 8, color: '#185FA5', borderColor: '#185FA5' }} onClick={() => setShowAuth(true)}>
              Sign in to save places
            </button>
          </div>
        ) : favLoading ? (
          <p style={s.mutedText}>Loading...</p>
        ) : favorites.length === 0 ? (
          <div style={s.emptyState}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>📍</div>
            <p style={s.mutedText}>No saved spots yet. Save your first below to track it here.</p>
          </div>
        ) : (
          favorites.map(f => (
            <div key={f.id} style={s.favItem}>
              <button style={s.favName} onClick={() => { setLocation({ name: f.name, lat: f.lat, lon: f.lon }); if (isMobile) setShowSidebar(false); }}>{f.name}</button>
              <button style={s.favRemove} onClick={() => removeFavorite(f.id)}>✕</button>
            </div>
          ))
        )}
        {user && (
          <button style={s.saveBtn} onClick={saveFavorite}>☆ Save current location</button>
        )}
        {user && favorites.length >= 2 && (
          <button
            style={{ ...s.saveBtn, marginTop: 8, position: 'relative' }}
            onClick={() => {
              isPro ? setShowCompare(true) : setShowUpgradeNudge(true);
              if (isMobile) setShowSidebar(false);
            }}
          >
            ⚖ Compare saved spots {!isPro && <span style={{ ...s.ratioProTag, position: 'static', marginLeft: 6 }}>PRO</span>}
          </button>
        )}
      </div>

      <div style={s.sideCard}>
        <div style={s.sectionLabel}>About SWE</div>
        <p style={s.sweText}><strong>Snow Water Equivalent</strong> is the depth of water that would result if all snowpack melted at once.</p>
        <div style={{ marginTop: 10, fontSize: 12, color: '#666', lineHeight: 1.8 }}>
          <div>6–8:1 · wet coastal snow</div>
          <div>10:1 · mid-mountain average</div>
          <div>12:1 · dry interior powder</div>
          <div>18:1+ · Colorado champagne</div>
        </div>
      </div>

      <div style={s.proPanel}>
        <div style={s.proPanelTitle}>Snow Logik Pro</div>
        {['Modeled SWE ratio','SNOTEL ground truth','Compare up to 5 spots','Unlimited saved places'].map(f => (
          <div key={f} style={s.proFeat}>✓ {f}</div>
        ))}
        <button style={s.proBtn} onClick={() => { user ? setShowPricing(true) : setShowAuth(true); if (isMobile) setShowSidebar(false); }}>
          Upgrade to Pro ↗
        </button>
      </div>

      <AdSlot slot="sidebar" sizeLabel="300 × 250" />

      {(() => {
        const ua = navigator.userAgent || '';
        const isIOS = /iPhone|iPad|iPod/.test(ua);
        const isAndroid = /Android/.test(ua);
        if (!isIOS && !isAndroid) return null; // desktop — this only applies on mobile
        return (
          <div style={s.installHint}>
            <span style={s.installHintIcon}>📲</span>
            <span>
              {isIOS
                ? 'Tap the Share icon, then "Add to Home Screen" for one-tap access all season.'
                : 'Tap the ⋮ menu, then "Add to Home Screen" for one-tap access all season.'}
            </span>
          </div>
        );
      })()}
    </div>
  );

  if (window.location.pathname === '/privacy') {
    return <PrivacyPolicy />;
  }

  return (
    <div style={s.app}>
      <style>{`
        @keyframes slSkeletonPulse {
          0%   { opacity: 0.5; }
          50%  { opacity: 1; }
          100% { opacity: 0.5; }
        }
      `}</style>
      {/* Header */}
      <header style={s.header}>
        <div style={s.headerInner}>
          <div style={s.logo}>
            <svg width="36" height="36" viewBox="0 0 160.78 160.24" xmlns="http://www.w3.org/2000/svg">
              <rect fill="#74b4e3" width="160.78" height="160.24" rx="4.39" ry="4.39"/>
              <g>
                <path fill="#fff" d="M130.84,102.12c-1.71-.47-3.43-.91-5.15-1.36-2.24-.59-4.48-1.18-7.14-1.88,2.35-2.3,4.32-4.31,6.38-6.22,1.52-1.41,2.39-3.39,1.88-4.71-1.11-2.88-4.01-3.63-6.38-1.46-2.77,2.53-5.36,5.26-8.01,7.92-1.42,1.43-2.7,2.36-5.08,1.61-5.55-1.75-11.24-3.05-16.87-4.54-.56-.15-1.1-.35-1.78-.57.3-.48.42-.78.64-1,4.32-4.42,8.66-8.82,12.98-13.25,1.26-1.29,2.45-1.85,4.46-1.27,4.03,1.17,8.18,1.94,12.31,2.74,1.85.36,3.9-1.09,4.18-2.67.33-1.85-.96-3.96-2.8-4.47-1.98-.55-3.97-1.04-5.96-1.55-1.2-.31-2.4-.63-3.92-1.03,1.69-1.7,3.1-3.07,4.46-4.49,1.69-1.76,3.55-3.41,4.9-5.41.57-.84.37-2.62-.14-3.64-.54-1.07-1.58-2.4-3.28-1.68-.11.05-.22.09-.33.13L41.61,127.87c-.03.07-.07.13-.09.2-.51,1.46.85,3.78,2.27,4.52,1.04.54,3.51-.16,4.58-1.26,2.81-2.9,5.63-5.78,8.64-8.86.27.59.52.97.64,1.39.72,2.65,1.41,5.31,2.11,7.97.59,2.21,2.99,3.84,4.88,3.33,1.81-.49,2.94-2.71,2.38-4.99-1.07-4.42-2.25-8.81-3.32-13.22-.11-.43.05-1.12.36-1.44,4.67-4.84,9.38-9.64,14.08-14.46.59-.6,1.03-.6,1.27.27,1.73,6.23,3.48,12.45,5.18,18.69.31,1.15.52,2.21-.67,3.29-2.71,2.47-5.28,5.11-7.76,7.83-1.31,1.44-3.14,2.73-2.31,5.28.47,1.42,2.2,2.63,3.7,2.59,2.08-.05,3.05-1.55,4.27-2.76,1.77-1.76,3.5-3.55,5.48-5.57,1.14,4.18,2.16,7.87,3.14,11.57.7,2.61,2.52,3.81,5,3.23,1.83-.43,2.96-2.84,2.37-5.12-.37-1.42-.79-2.82-1.17-4.23-.64-2.39-1.27-4.78-1.98-7.46.96.25,1.54.4,2.12.56,2.46.67,4.92,1.37,7.39,2,1.87.48,4.6-.99,5.06-2.66.52-1.87-.86-4.12-2.95-4.7-3.74-1.03-7.46-2.2-11.27-2.96-2.24-.44-3.07-1.73-3.57-3.64-1.34-5.03-2.71-10.06-4.05-15.09-.31-1.16-.51-2.35-.81-3.82.98.26,1.57.41,2.15.57,5.53,1.46,11.02,3.07,16.59,4.31,2.37.53,3.4,1.63,3.92,3.92.82,3.63,1.98,7.17,3.01,10.75.69,2.4,2.94,3.11,4.68,2.78,1.77-.34,3.28-3.08,2.75-4.84-.65-2.19-1.38-4.36-2.03-6.55-.26-.86-.39-1.76-.61-2.81.6.11.87.15,1.13.22,3.6.94,7.18,1.92,10.79,2.83,1.94.49,4.32-.85,4.84-2.64.54-1.85-.93-4.22-2.97-4.78Z"/>
                <path fill="#fff" d="M34.53,75.76c.07-3.15.99-6.44,2.22-9.38,6.58-15.71,17.21-28.74,28.08-42.14,10.27,12.48,19.59,24.89,26.86,38.84,2.05,3.93,3.08,7.87,3.3,11.69l6.75-6.75c-.6-2.38-1.45-4.75-2.61-7.09-8.16-16.54-19.2-30.93-31.19-44.72-1.89-2.17-4.34-2.09-6.3.1-2.83,3.17-5.6,6.41-8.25,9.75-9.03,11.35-17.81,22.92-23.35,36.46-1.69,4.14-3.13,8.75-3.16,13.16-.13,18.3,12.91,33.46,30.16,37.06l6.83-6.83c-15.69-.66-29.67-13.88-29.33-30.15Z"/>
                <path fill="#fff" d="M64.26,90.68c-8.97-1.13-13.8-6.08-14.6-14.96-.23-2.58-1.55-3.94-4.01-4.01-2.25-.06-3.77,2.01-3.71,4.66.26,11.38,9.83,21.27,21.18,21.9,3.83.21,5.82-1.31,5.44-4.21-.33-2.5-2.08-3.11-4.3-3.39Z"/>
              </g>
            </svg>
            <div>
              <div style={s.logoText}>Snow Logik</div>
              {!isMobile && <div style={s.logoSub}>Know the snow, before you go.</div>}
            </div>
          </div>
          <div style={s.headerRight}>
            <button style={s.unitToggle} onClick={() => setImperial(!imperial)}>
              <span style={{ color: !imperial ? '#185FA5' : '#999', fontWeight: !imperial ? 600 : 400 }}>cm</span>
              <span style={{ color: '#ccc', margin: '0 4px' }}>|</span>
              <span style={{ color: imperial ? '#185FA5' : '#999', fontWeight: imperial ? 600 : 400 }}>in</span>
            </button>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {!isMobile && (
                  <span style={{ fontSize: 12, color: '#64748b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.email}
                  </span>
                )}
                <button style={s.unitToggle} onClick={handleSignOut}>Sign out</button>
                {!isMobile && !isPro && (
                  <button style={{ ...s.proBadge, background: '#042C53' }} onClick={() => setShowPricing(true)}>Pro ↗</button>
                )}
              </div>
            ) : (
              <>
                {!isMobile && <button style={s.unitToggle} onClick={() => setShowAuth(true)}>Sign in</button>}
                <button style={{ ...s.proBadge, background: '#042C53' }} onClick={() => setShowPricing(true)}>Pro ↗</button>
              </>
            )}
            {isMobile && (
              <button style={s.menuBtn} onClick={() => setShowSidebar(true)}>☰</button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div style={s.hero}>
        {/* Hero photo */}
        <div style={{
          ...s.heroIllustration,
          backgroundImage: 'url(/images/hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
        }}>
          {/* Dark overlay so text stays readable */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to right, rgba(10,30,70,0.75) 0%, rgba(10,30,70,0.45) 60%, rgba(10,30,70,0.2) 100%)',
          }}/>
        </div>

        {/* Hero content */}
        <div style={s.heroContent}>
          <div style={s.heroEyebrow}>Snow Water Equivalent Forecast</div>
          <h1 style={{ ...s.heroTitle, fontSize: isMobile ? 32 : 48 }}>Know the snow,<br/>before you go.</h1>
          <p style={s.heroSub}>
            Snow Logik gives you the tools to model how much actual snow to expect in an infinite number of places. Built for skiers, snowboarders, and anyone moving through the mountains.
          </p>
          <div style={s.heroActions}>
            <button style={s.heroCTA} onClick={() => document.getElementById('app-start').scrollIntoView({ behavior: 'smooth' })}>
              Check the forecast ↓
            </button>
            <button style={s.heroSecondary} onClick={() => user ? setShowPricing(true) : setShowAuth(true)}>
              Upgrade to Pro
            </button>
          </div>
          {CLIMATE_PLEDGE_ENABLED && (
            <div style={s.climateBadge}>
              <img src="/badges/stripe-climate.svg" alt="Stripe Climate" style={s.climateBadgeIcon} />
              1% of every Pro subscription funds carbon removal, via Stripe Climate
            </div>
          )}
          <div style={s.heroStats}>
            <div style={s.heroStat}><span style={s.heroStatNum}>14</span><span style={s.heroStatLabel}>day outlook</span></div>
            <div style={s.heroStatDivider}/>
            <div style={s.heroStat}><span style={s.heroStatNum}>4</span><span style={s.heroStatLabel}>density ratios</span></div>
            <div style={s.heroStatDivider}/>
            <div style={s.heroStat}><span style={s.heroStatNum}>∞</span><span style={s.heroStatLabel}>locations</span></div>
          </div>
        </div>
      </div>

      {/* App anchor */}
      <div id="app-start" />

      {/* Search + chips -- now lives close to the map */}
      <div style={s.searchBar}>
        <input
          style={s.searchInput}
          placeholder="Search resort, mountain, or city..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && handleSearch()}
        />
        <button style={s.searchBtn} onClick={handleSearch}>Go</button>
      </div>
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
        <div style={s.chipRow}>
          <span style={s.chipGroupLabel}>⛷ {REGION_SETS[chipRegion].label}</span>
          {REGION_SETS[chipRegion].resorts.map(r => (
            <button
              key={r.name}
              style={{ ...s.chip, ...(location.name === r.name ? s.chipActive : {}) }}
              onClick={() => setLocation(r)}
            >
              {r.name}
            </button>
          ))}
          <span style={{ ...s.chipGroupLabel, marginLeft: 8 }}>🏔 Passes</span>
          {REGION_SETS[chipRegion].passes.map(p => (
            <button
              key={p.name}
              style={{ ...s.chip, ...(location.name === p.name ? s.chipPassActive : {}) }}
              onClick={() => setLocation(p)}
            >
              {p.name}
            </button>
          ))}
          <select
            value={chipRegion}
            onChange={e => setChipRegion(e.target.value)}
            style={s.regionSelect}
            title="Show chips for a different region"
          >
            {Object.entries(REGION_SETS).map(([key, r]) => (
              <option key={key} value={key}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main layout */}
      <div style={{ ...s.mainGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 260px' }}>
        <div style={s.content}>

          {/* Map */}
          <div style={s.card}>
            <div style={s.sectionLabel}>Location</div>
            <MapView location={location} onLocationChange={setLocation} />
          </div>

          {/* Hero data */}
          <div style={s.card}>
            <div style={s.locationLine}>
              <span style={s.locationName}>{location.name}</span>
              <span style={s.locationMeta}>
                {location.lat.toFixed(2)}°N · {Math.abs(location.lon).toFixed(2)}°W
                {typeof weather?.elevation === 'number' && (
                  <> · {imperial ? `${Math.round(weather.elevation * 3.28084).toLocaleString()} ft` : `${Math.round(weather.elevation)} m`}</>
                )}
              </span>
              <span style={s.liveTag}>● Live</span>
            </div>
            <div style={s.periodTabs}>
              {PERIODS.map(p => (
                <button
                  key={p.key}
                  style={{ ...s.periodTab, ...(period === p.key ? s.periodTabActive : {}) }}
                  onClick={() => setPeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {loading ? (
              <div>
                <div style={s.skeletonBig} />
                <div style={{ ...s.skeletonLine, width: 160, marginBottom: 16 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div style={s.skeletonCell} />
                  <div style={s.skeletonCell} />
                  <div style={s.skeletonCell} />
                </div>
              </div>
            ) : error ? (
              <div style={s.errorState}>{error}</div>
            ) : (
              <>
                <div style={s.bigReadout}>
                  <span style={{ ...s.bigNumber, fontSize: isMobile ? 52 : 68 }}>{fmt(dispSnow)}</span>
                  <span style={{ ...s.bigUnit, fontSize: isMobile ? 24 : 30 }}>{u}</span>
                </div>
                {dispSnow > 0 && (() => {
                  const type = snowTypeLabel(effectiveRatio);
                  return (
                    <div style={{ ...s.snowTypeBadge, color: type.color, borderColor: type.color }}>
                      {type.label}
                      <span style={s.snowTypeCaveat}>· new snow, not current conditions</span>
                    </div>
                  );
                })()}
                <div style={s.bigLabel}>projected snowfall · {PERIODS.find(p => p.key === period)?.label} · {ratio === 'modeled' ? `${modeledRatio.toFixed(1)}:1 modeled` : `${ratio}:1 ratio`}</div>
                <div style={{ ...s.statRow, gridTemplateColumns: '1fr 1fr 1fr' }}>
                  <div style={s.statCell}>
                    <div style={s.statLabel}>Liquid precip</div>
                    <div style={s.statVal}>{fmt2(dispPrecip)} {u}</div>
                  </div>
                  <div style={s.statCell}>
                    <div style={s.statLabel}>Elevation</div>
                    <div style={s.statVal}>
                      {typeof weather?.elevation === 'number'
                        ? (imperial ? `${Math.round(weather.elevation * 3.28084).toLocaleString()} ft` : `${Math.round(weather.elevation)} m`)
                        : '—'}
                    </div>
                  </div>
                  <div style={s.statCell}>
                    <div style={s.statLabel}>{ratio === 'modeled' ? 'Modeled ratio' : 'SWE ratio'}</div>
                    <div style={s.statVal}>{ratio === 'modeled' ? `${modeledRatio.toFixed(1)}:1` : `${ratio}:1`}</div>
                  </div>
                </div>
                <button style={s.shareBtn} onClick={handleShare}>
                  {shareCopied ? '✓ Copied link' : '↗ Share this forecast'}
                </button>
              </>
            )}
          </div>

          {/* Ratio selector */}
          <div style={s.card}>
            <div style={s.sectionLabel}>Snow density ratio</div>
            <div style={s.ratioGrid}>
              {RATIOS.map(r => (
                <button
                  key={r.value}
                  style={{ ...s.ratioBtn, ...(ratio === r.value ? s.ratioBtnActive : {}) }}
                  onClick={() => setRatio(r.value)}
                >
                  <span style={s.ratioSymbol}>{r.symbol}</span>
                  <div style={{ ...s.ratioNum, ...(ratio === r.value ? { color: '#185FA5' } : {}) }}>{r.label}</div>
                  <div style={{ ...s.ratioDesc, ...(ratio === r.value ? { color: '#378ADD' } : {}) }}>{r.desc}</div>
                </button>
              ))}
              <button
                key="modeled"
                style={{ ...s.ratioBtn, ...(ratio === 'modeled' ? s.ratioBtnActive : {}), position: 'relative' }}
                onClick={() => { isPro ? setRatio('modeled') : setShowUpgradeNudge(true); }}
              >
                {!isPro && <span style={s.ratioProTag}>PRO</span>}
                <span style={s.ratioSymbol}>🌡</span>
                <div style={{ ...s.ratioNum, ...(ratio === 'modeled' ? { color: '#185FA5' } : {}) }}>Modeled</div>
                <div style={{ ...s.ratioDesc, ...(ratio === 'modeled' ? { color: '#378ADD' } : {}) }}>Live temp model</div>
              </button>
            </div>
            {ratio === 'modeled' && (
              <p style={s.modeledNote}>
                Estimated from real-time surface temperature at this location, weighted by when precip actually falls. A modeled guess, not a guarantee, wind and terrain still move the needle on the ground.
              </p>
            )}
          </div>

          {/* SNOTEL ground truth */}
          <div style={s.card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={s.sectionLabel}>SNOTEL ground truth</div>
              {!isPro && <span style={{ ...s.ratioProTag, position: 'static' }}>PRO</span>}
            </div>
            {!isPro && (
              <p style={s.modeledNote}>
                See real, physically-measured snowpack from the nearest USDA SNOTEL station, not a forecast. <button style={s.inlineUpgradeBtn} onClick={() => setShowUpgradeNudge(true)}>Upgrade to Pro</button> to unlock it.
              </p>
            )}
            {isPro && snotelLoading && (
              <p style={s.modeledNote}>Checking the nearest SNOTEL station…</p>
            )}
            {isPro && !snotelLoading && snotel?.nearest && (
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{snotel.nearest.name}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
                  {snotel.distanceMiles} mi away · {snotel.nearest.elevation?.toLocaleString()} ft
                </div>
                <div style={{ ...s.statRow, gridTemplateColumns: '1fr 1fr' }}>
                  <div style={s.statCell}>
                    <div style={s.statLabel}>Current SWE</div>
                    <div style={s.statVal}>{typeof snotel.sweInches === 'number' ? `${snotel.sweInches.toFixed(1)} in` : '—'}</div>
                  </div>
                  <div style={s.statCell}>
                    <div style={s.statLabel}>Snow depth</div>
                    <div style={s.statVal}>{typeof snotel.snowDepthInches === 'number' ? `${snotel.snowDepthInches} in` : '—'}</div>
                  </div>
                </div>
              </div>
            )}
            {isPro && !snotelLoading && snotel && !snotel.nearest && (
              <p style={s.modeledNote}>
                No SNOTEL station within range of this location. SNOTEL is a US network, so this is expected outside the western US and in Canada.
              </p>
            )}
          </div>

          {/* Hourly chart */}
          {(period === '24hr' || period === '48hr') && weather?.hourly && (
            <div style={s.card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={s.sectionLabel}>Hourly snowfall forecast</div>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{ratio === 'modeled' ? `~${modeledRatio.toFixed(1)}:1 modeled avg` : `${ratio}:1 ratio`} · {u}</span>
              </div>
              <HourlyChart
                hourly={weather.hourly}
                hours={period === '24hr' ? 24 : 48}
                ratio={ratio}
                toDisplay={toDisplay}
                u={u}
                imperial={imperial}
              />
            </div>
          )}

          {/* 14-day forecast */}
          <div style={s.card}>
            <div style={s.sectionLabel}>14-day precipitation outlook</div>
            {weather?.daily ? weather.daily.precipitation_sum.map((p, i) => {
              const precDay = parseFloat(p) || 0;
              let dayRatio = ratio === 'modeled' ? modeledRatio : ratio;
              if (ratio === 'modeled' && weather.hourly) {
                const dayPrecip = weather.hourly.precipitation.slice(i * 24, i * 24 + 24);
                const dayTemps  = weather.hourly.temperature_2m.slice(i * 24, i * 24 + 24);
                dayRatio = computeModeledRatio(dayPrecip, dayTemps);
              }
              const snowDay = precDay * dayRatio;
              const pct     = (precDay / maxDailyPrecip) * 100;
              const date    = new Date(); date.setDate(date.getDate() + i);
              const label   = i === 0 ? 'Today' : i === 1 ? 'Tmrw' : dayNames[date.getDay()];
              const hi      = weather.daily.temperature_2m_max?.[i];
              const lo      = weather.daily.temperature_2m_min?.[i];
              const hiD     = imperial ? hi : (hi - 32) * 5 / 9;
              const loD     = imperial ? lo : (lo - 32) * 5 / 9;
              return (
                <div key={i} style={s.dayRow}>
                  <span style={s.dayName}>{label}</span>
                  <div style={s.dayBarWrap}><div style={{ ...s.dayBar, width: `${Math.max(pct, 2)}%` }} /></div>
                  <span style={s.dayPrecip}>{fmt2(toDisplay(precDay))}{u}</span>
                  <span style={s.daySnow}>{fmt(toDisplay(snowDay))}{u}</span>
                  {hi != null && !isMobile && <span style={s.dayTemp}>{Math.round(hiD)}°/{Math.round(loD)}°</span>}
                </div>
              );
            }) : <div style={s.loadingState}>Loading...</div>}
          </div>

          {/* Mobile Pro CTA */}
          {isMobile && (
            <div style={s.proPanel}>
              <div style={s.proPanelTitle}>Snow Logik Pro</div>
              {['Hourly SWE breakdown','SNOTEL station data','Powder email alerts'].map(f => (
                <div key={f} style={s.proFeat}>✓ {f}</div>
              ))}
              <button style={s.proBtn} onClick={() => user ? setShowPricing(true) : setShowAuth(true)}>Upgrade to Pro ↗</button>
            </div>
          )}

          {/* Ad slot */}
          <AdSlot slot="banner" sizeLabel={isMobile ? '320 × 50 mobile banner' : '728 × 90 — Ski resort, gear brand & outdoor apparel'} />

        </div>

        {/* Desktop sidebar */}
        {!isMobile && <Sidebar />}
      </div>

      {/* Mobile sidebar drawer */}
      {isMobile && showSidebar && (
        <div style={s.drawerOverlay} onClick={() => setShowSidebar(false)}>
          <div style={s.drawer} onClick={e => e.stopPropagation()}>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Auth modal */}
      {showAuth && (
        <AuthModal onClose={() => setShowAuth(false)} onAuth={setUser} />
      )}

      {/* Pricing modal */}
      {showPricing && (
        <PricingModal onClose={() => setShowPricing(false)} user={user} />
      )}

      {/* Upgrade nudge modal */}
      {showUpgradeNudge && (
        <div style={s.modalOverlay} onClick={() => setShowUpgradeNudge(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <button style={s.modalClose} onClick={() => setShowUpgradeNudge(false)}>✕</button>
            <div style={s.modalTitle}>You've hit your limit ⛷</div>
            <p style={s.modalText}>
              Free accounts can save up to {FREE_PLACE_LIMIT} locations. Upgrade to Snow Logik Pro for unlimited saved places, powder alerts, SNOTEL data, and more. Just $1.99/month or $9.99 for the season.
            </p>
            <button style={{ ...s.modalBtn, marginTop: 16 }} onClick={() => { setShowUpgradeNudge(false); setShowPricing(true); }}>
              Upgrade to Pro ↗
            </button>
            <button style={{ ...s.modalBtn, marginTop: 8, background: '#f1f5f9', color: '#64748b' }} onClick={() => setShowUpgradeNudge(false)}>
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Compare modal */}
      {showCompare && (
        <div style={s.modalOverlay} onClick={() => setShowCompare(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <button style={s.modalClose} onClick={() => setShowCompare(false)}>✕</button>
            <div style={s.modalTitle}>Compare saved spots</div>

            {!compareResults && (
              <>
                <p style={{ ...s.modalText, marginBottom: 12 }}>
                  Pick 2–{MAX_COMPARE} saved locations and a ratio, then run the comparison for the {PERIODS.find(p => p.key === period)?.label} window.
                </p>
                <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 14 }}>
                  {favorites.map(f => {
                    const checked = compareSelected.includes(f.id);
                    const disabled = !checked && compareSelected.length >= MAX_COMPARE;
                    return (
                      <label
                        key={f.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 4px', fontSize: 14,
                          color: disabled ? '#cbd5e1' : '#0f172a',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleCompareSelect(f.id)}
                        />
                        {f.name}
                      </label>
                    );
                  })}
                </div>

                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>SNOW DENSITY RATIO</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 16 }}>
                  {RATIOS.map(r => (
                    <button
                      key={r.value}
                      style={{ ...s.ratioBtn, padding: '6px 2px', ...(compareRatio === r.value ? s.ratioBtnActive : {}) }}
                      onClick={() => setCompareRatio(r.value)}
                    >
                      <div style={{ ...s.ratioNum, fontSize: 12, ...(compareRatio === r.value ? { color: '#185FA5' } : {}) }}>{r.label}</div>
                    </button>
                  ))}
                  <button
                    style={{ ...s.ratioBtn, padding: '6px 2px', ...(compareRatio === 'modeled' ? s.ratioBtnActive : {}) }}
                    onClick={() => setCompareRatio('modeled')}
                  >
                    <div style={{ ...s.ratioNum, fontSize: 12, ...(compareRatio === 'modeled' ? { color: '#185FA5' } : {}) }}>🌡</div>
                  </button>
                </div>

                <button
                  style={{ ...s.modalBtn, opacity: compareSelected.length < 2 ? 0.5 : 1 }}
                  disabled={compareSelected.length < 2 || compareLoading}
                  onClick={runCompare}
                >
                  {compareLoading ? 'Comparing…' : `Compare ${compareSelected.length || ''} spots`}
                </button>
              </>
            )}

            {compareResults && (
              <>
                {compareResults.length === 0 ? (
                  <p style={s.modalText}>Couldn't load the comparison. Please try again.</p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr repeat(4, 52px)', gap: 4, marginBottom: 4 }}>
                      <div />
                      {PERIODS.map(p => (
                        <div key={p.key} style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>
                          {p.label}
                        </div>
                      ))}
                    </div>
                    {compareResults.map((r, i) => (
                      <div
                        key={r.id}
                        style={{
                          display: 'grid', gridTemplateColumns: '1fr repeat(4, 52px)', gap: 4, alignItems: 'center',
                          padding: '10px 0', borderBottom: i < compareResults.length - 1 ? '1px solid #f1f5f9' : 'none',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                            {i === 0 && '🥇 '}{r.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {r.elevation ? `${Math.round(r.elevation * 3.28084).toLocaleString()} ft · ` : ''}
                            {compareRatio === 'modeled' ? 'modeled' : `${compareRatio}:1`}
                          </div>
                        </div>
                        {PERIODS.map(p => (
                          <div key={p.key} style={{ fontSize: 14, fontWeight: 700, color: '#185FA5', fontFamily: 'monospace', textAlign: 'center' }}>
                            {fmt(r.byPeriod[p.key].snow)}
                          </div>
                        ))}
                      </div>
                    ))}
                    <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 8 }}>
                      Values in {u} · ranked by 2-week total
                    </div>
                  </>
                )}
                <button
                  style={{ ...s.modalBtn, marginTop: 16, background: '#f1f5f9', color: '#64748b' }}
                  onClick={() => setCompareResults(null)}
                >
                  Back
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Why Go Pro Section */}
      <section style={s.whyPro}>
        <div style={s.whyProInner}>
          <h2 style={s.whyProTitle}>Why Go Pro</h2>
          <p style={s.whyProSubtitle}>
            Four things you can't get on the free tier, built for people who take their snow seriously.
          </p>

          <div style={{ ...s.whyProGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)' }}>
            <div style={s.whyProCard}>
              <div style={s.whyProCardIcon}>🌡</div>
              <div style={s.whyProCardTitle}>Modeled SWE ratio</div>
              <div style={s.whyProCardText}>
                A live, temperature-modeled snow ratio for every hour of the forecast, built from real atmospheric data instead of one static guess.
              </div>
            </div>
            <div style={s.whyProCard}>
              <div style={s.whyProCardIcon}>📡</div>
              <div style={s.whyProCardTitle}>SNOTEL ground truth</div>
              <div style={s.whyProCardText}>
                Check our numbers against real, physically measured snowpack from the nearest government sensor station. Not a model, an actual scale on the mountain.
              </div>
            </div>
            <div style={s.whyProCard}>
              <div style={s.whyProCardIcon}>⚖</div>
              <div style={s.whyProCardTitle}>Compare up to 5 spots</div>
              <div style={s.whyProCardText}>
                Line up your saved locations side by side across all four forecast windows and see exactly where a storm cycle hits hardest.
              </div>
            </div>
            <div style={s.whyProCard}>
              <div style={s.whyProCardIcon}>📍</div>
              <div style={s.whyProCardTitle}>Unlimited saved places</div>
              <div style={s.whyProCardText}>
                Track every zone you actually ski, not just three. Front range, backcountry, wherever you chase it.
              </div>
            </div>
          </div>

          <div style={s.whyProPricing}>
            <div style={s.whyProPrice}>
              <span style={s.whyProPriceNum}>$1.99</span>
              <span style={s.whyProPriceUnit}>/month</span>
              <span style={s.whyProPriceNote}>cancel anytime, ski bum safe</span>
            </div>
            <div style={s.whyProPriceDivider}>or</div>
            <div style={s.whyProPrice}>
              <span style={s.whyProPriceNum}>$9.99</span>
              <span style={s.whyProPriceUnit}>/season</span>
              <span style={s.whyProPriceNote}>one-time, the six pack</span>
            </div>
          </div>

          <button
            style={s.whyProCta}
            onClick={() => user ? setShowPricing(true) : setShowAuth(true)}
          >
            Upgrade to Pro ↗
          </button>
        </div>
      </section>

      {/* About Section */}
      <section style={s.about}>
        <div
          style={{
            ...s.aboutBanner,
            backgroundImage: 'url(/images/about-banner.jpg)',
          }}
        />
        <div style={s.aboutCopy}>
          <div style={s.aboutInner}>
            <h2 style={s.aboutTitle}>About Snow Logik</h2>
            <p style={s.aboutText}>
              Snow Logik started as a joke. During an off-season storm, we ran
              the rain through a snow ratio calculator just to text a buddy:
              "bro, if this were snow." The number came back stupid enough to
              screenshot. We couldn't stop doing it.
            </p>
            <p style={s.aboutText}>
              Then we realized the tool underneath the joke was actually
              useful. The same math that separates 18 inches of blower pow
              from 6 inches of wet concrete works whether it's a July storm
              or a January one. So we built it out for real.
            </p>
            <p style={s.aboutText}>
              Snow Logik isn't tied to resorts. Pin your house, a buddy's
              cabin, a backcountry hut, your actual stash, and it runs the
              same real forecast a resort report never will.
            </p>
            <p style={s.aboutText}>
              Between us we've got time inside tech companies, Fortune 500
              marketing teams, and outdoor brand building. We put that into
              something we're genuinely proud of, and we hope you find it
              useful out there.
            </p>
          </div>
        </div>
      </section>

      {/* Why This Section */}
      <section style={s.whyThis}>
        <div style={s.whyThisInner}>
          <h2 style={s.whyThisTitle}>Why This Exists</h2>
          <p style={s.whyThisText}>
            Most snow forecasts hide the work. You get a range, "4"-8","
            with no idea where it came from: what the actual temperature was,
            what assumptions turned rain-or-shine precipitation into a snow
            total, or whether that number is a forecast, an outdated report,
            or someone eyeballing a webcam.
          </p>
          <p style={s.whyThisText}>
            We built Snow Logik to show the work instead of hiding it. No
            black box, no "trust the algorithm." Just the actual inputs, the
            actual math, and a real sensor to check it against.
          </p>

          <div style={{ ...s.whyThisSteps, gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)' }}>
            <div style={s.whyThisStep}>
              <div style={s.whyThisStepIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 19V10M12 19V4M20 19V13" stroke="#185FA5" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={s.whyThisStepTitle}>Real atmospheric data</div>
              <div style={s.whyThisStepText}>
                Every forecast starts with the same hourly precipitation and
                temperature data for wherever you're headed. No guessing.
              </div>
            </div>
            <div style={s.whyThisStep}>
              <div style={s.whyThisStepIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="8" stroke="#185FA5" strokeWidth="2"/>
                  <path d="M12 8v4l3 2" stroke="#185FA5" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={s.whyThisStepTitle}>Your call on the ratio</div>
              <div style={s.whyThisStepText}>
                Pick a fixed ratio and see the range yourself, or let our
                temp-modeled ratio estimate density hour by hour. Either way,
                you see how the number was built.
              </div>
            </div>
            <div style={s.whyThisStep}>
              <div style={s.whyThisStepIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 13l4 4L19 7" stroke="#185FA5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={s.whyThisStepTitle}>Checked against reality</div>
              <div style={s.whyThisStepText}>
                We pull live SNOTEL data, real snow sensors buried in the
                mountains, so you can see how our numbers hold up, not just
                take our word for it.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={s.footer}>
        <div style={s.footerTagline}>Know the snow, before you go.</div>
        <div style={s.footerLinks}>
          <a href="/privacy" style={s.footerLink}>Privacy Policy</a>
          <span style={s.footerDivider}>·</span>
          <span style={s.footerLink}>© {new Date().getFullYear()} Snow Logik, LLC</span>
        </div>
        {CLIMATE_PLEDGE_ENABLED && (
          <div style={s.footerClimateBadge}>
            <img src="/badges/stripe-climate.svg" alt="Stripe Climate" style={s.footerClimateIcon} />
            1% pledged to carbon removal via Stripe Climate
          </div>
        )}
      </footer>
    </div>
  );
}

const s = {
  hero:            { position: 'relative', overflow: 'hidden', minHeight: 420, display: 'flex', alignItems: 'center', background: '#0C447C' },
  heroIllustration:{ position: 'absolute', inset: 0, zIndex: 0 },
  heroContent:     { position: 'relative', zIndex: 1, maxWidth: 1100, margin: '0 auto', padding: '48px 24px 40px', width: '100%' },
  heroEyebrow:     { fontSize: 12, fontWeight: 600, color: '#85B7EB', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 12 },
  heroTitle:       { fontSize: 48, fontWeight: 700, color: '#ffffff', lineHeight: 1.1, marginBottom: 16, letterSpacing: '-1px' },
  heroSub:         { fontSize: 17, color: '#B5D4F4', lineHeight: 1.6, maxWidth: 520, marginBottom: 28 },
  heroActions:     { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 },
  climateBadge:    { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#DCE9F7', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999, padding: '6px 14px', marginTop: -18, marginBottom: 28, width: 'fit-content' },
  climateBadgeIcon: { width: 15, height: 15, flexShrink: 0 },
  footerClimateBadge: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11.5, color: '#94a3b8', marginTop: 10 },
  footerClimateIcon: { width: 13, height: 13, flexShrink: 0 },
  heroCTA:         { padding: '12px 24px', background: '#ffffff', color: '#0C447C', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  heroSecondary:   { padding: '12px 24px', background: 'transparent', color: '#ffffff', border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 10, fontSize: 15, fontWeight: 500, cursor: 'pointer' },
  heroStats:       { display: 'flex', alignItems: 'center', gap: 16 },
  heroStat:        { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  heroStatNum:     { fontSize: 22, fontWeight: 700, color: '#ffffff', lineHeight: 1 },
  heroStatLabel:   { fontSize: 11, color: '#85B7EB', marginTop: 2 },
  heroStatDivider: { width: 1, height: 32, background: 'rgba(255,255,255,0.2)' },
  app:          { minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: '#F4F6F9', color: '#1a1a1a' },
  header:       { background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 100 },
  headerInner:  { maxWidth: 1100, margin: '0 auto', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo:         { display: 'flex', alignItems: 'center', gap: 10 },
  logoText:     { fontSize: 17, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.3px' },
  logoSub:      { fontSize: 11, color: '#94a3b8' },
  headerRight:  { display: 'flex', alignItems: 'center', gap: 8 },
  unitToggle:   { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 13 },
  proBadge:     { background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  menuBtn:      { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 18, color: '#0f172a' },
  searchBar:    { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '8px 16px', display: 'flex', gap: 8 },
  searchInput:  { flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff' },
  searchBtn:    { padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  chipRow:      { display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 16px', background: '#fff', borderBottom: '1px solid #e5e7eb', scrollbarWidth: 'none' },
  chip:         { padding: '5px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 20, fontSize: 12, color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  chipActive:      { background: '#E6F1FB', borderColor: '#378ADD', color: '#185FA5', fontWeight: 500 },
  chipPassActive:  { background: '#FFF7ED', borderColor: '#F97316', color: '#C2410C', fontWeight: 500 },
  chipGroupLabel:  { fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', paddingRight: 2, flexShrink: 0 },
  regionSelect:    { marginLeft: 8, fontSize: 11, color: '#94a3b8', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 6px', cursor: 'pointer', flexShrink: 0 },
  mainGrid:     { maxWidth: 1100, margin: '0 auto', padding: '12px 16px', display: 'grid', gap: 16 },
  content:      { display: 'flex', flexDirection: 'column', gap: 12 },
  card:         { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px' },
  locationLine: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  locationName: { fontSize: 15, fontWeight: 600, color: '#0f172a' },
  locationMeta: { fontSize: 11, color: '#94a3b8' },
  liveTag:      { marginLeft: 'auto', fontSize: 11, color: '#16a34a', background: '#f0fdf4', padding: '2px 8px', borderRadius: 4 },
  periodTabs:   { display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' },
  periodTab:    { padding: '6px 12px', borderRadius: 8, fontSize: 13, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer' },
  periodTabActive: { background: '#185FA5', color: '#fff', borderColor: '#185FA5' },
  bigReadout:   { display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 4 },
  bigNumber:    { fontSize: 68, fontWeight: 600, lineHeight: 1, color: '#0f172a', fontFamily: 'monospace' },
  bigUnit:      { fontSize: 30, color: '#64748b', fontFamily: 'monospace', paddingBottom: 8 },
  bigLabel:     { fontSize: 12, color: '#94a3b8', marginBottom: 16 },
  snowTypeBadge: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, border: '1px solid', borderRadius: 999, padding: '4px 12px', marginBottom: 10 },
  snowTypeCaveat: { fontSize: 10.5, fontWeight: 400, color: '#94a3b8' },
  skeletonBig:  { width: 180, height: 68, borderRadius: 10, background: '#e2e8f0', animation: 'slSkeletonPulse 1.4s ease-in-out infinite', marginBottom: 10 },
  skeletonLine: { height: 12, borderRadius: 6, background: '#e2e8f0', animation: 'slSkeletonPulse 1.4s ease-in-out infinite' },
  skeletonCell: { height: 52, borderRadius: 8, background: '#e2e8f0', animation: 'slSkeletonPulse 1.4s ease-in-out infinite' },
  statRow:      { display: 'grid', gap: 8, marginTop: 4 },
  statCell:     { background: '#f8fafc', borderRadius: 8, padding: '10px 12px' },
  statLabel:    { fontSize: 10, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px' },
  statVal:      { fontSize: 15, fontWeight: 500, color: '#0f172a', fontFamily: 'monospace' },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 },
  ratioGrid:    { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 },
  ratioBtn:     { border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 2px', cursor: 'pointer', background: '#f8fafc', textAlign: 'center' },
  ratioBtnActive: { border: '2px solid #185FA5', background: '#E6F1FB' },
  ratioSymbol:  { fontSize: 17, display: 'block', marginBottom: 3 },
  ratioNum:     { fontSize: 14, fontWeight: 600, color: '#0f172a' },
  ratioDesc:    { fontSize: 10, color: '#94a3b8', marginTop: 2 },
  ratioProTag:  { position: 'absolute', top: 4, right: 4, fontSize: 8, fontWeight: 700, color: '#fff', background: '#185FA5', borderRadius: 4, padding: '1px 4px' },
  modeledNote:  { fontSize: 11.5, color: '#64748b', lineHeight: 1.5, marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 10 },
  inlineUpgradeBtn: { background: 'none', border: 'none', padding: 0, color: '#185FA5', fontWeight: 600, fontSize: 11.5, cursor: 'pointer', textDecoration: 'underline' },
  dayRow:       { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 8, background: '#f8fafc', marginBottom: 4 },
  dayName:      { fontSize: 11, color: '#64748b', width: 30, flexShrink: 0 },
  dayBarWrap:   { flex: 1, height: 5, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' },
  dayBar:       { height: 5, background: '#378ADD', borderRadius: 3, transition: 'width 0.4s' },
  dayPrecip:    { fontSize: 11, fontFamily: 'monospace', color: '#64748b', width: 40, textAlign: 'right' },
  daySnow:      { fontSize: 11, fontFamily: 'monospace', color: '#185FA5', fontWeight: 600, width: 40, textAlign: 'right' },
  dayTemp:      { fontSize: 11, color: '#94a3b8', width: 68, textAlign: 'right' },
  loadingState: { padding: '24px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 },
  errorState:   { padding: '14px', background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13 },
  adSlot:       { background: '#f8fafc', border: '1px dashed #d1d5db', borderRadius: 10, padding: '12px', textAlign: 'center' },
  adLabel:      { fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 },
  adText:       { fontSize: 12, color: '#94a3b8' },
  installHint:  { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#64748b', lineHeight: 1.5, background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: 10, padding: '12px' },
  installHintIcon: { fontSize: 16, flexShrink: 0 },
  adSlotFilled: { display: 'block', borderRadius: 10, overflow: 'hidden', lineHeight: 0 },
  adImage:      { width: '100%', height: 'auto', display: 'block' },
  sidebar:      { display: 'flex', flexDirection: 'column', gap: 12 },
  sidebarMobile:{ display: 'flex', flexDirection: 'column', gap: 12 },
  sideCard:     { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px' },
  mutedText:    { fontSize: 12, color: '#94a3b8' },
  emptyState:   { textAlign: 'center', padding: '16px 8px' },
  favItem:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' },
  favName:      { fontSize: 13, color: '#0f172a', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' },
  favRemove:    { fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' },
  saveBtn:      { marginTop: 8, width: '100%', padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, color: '#64748b', cursor: 'pointer' },
  sweText:      { fontSize: 12, color: '#64748b', lineHeight: 1.6 },
  proPanel:     { background: '#042C53', borderRadius: 12, padding: '16px' },
  proPanelTitle:{ fontSize: 13, fontWeight: 600, color: '#E6F1FB', marginBottom: 10 },
  proFeat:      { fontSize: 12, color: '#85B7EB', marginBottom: 5 },
  proBtn:       { marginTop: 10, width: '100%', padding: '9px', background: '#185FA5', color: '#E6F1FB', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  drawerOverlay:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 },
  drawer:       { position: 'absolute', right: 0, top: 0, bottom: 0, width: '85%', maxWidth: 320, background: '#F4F6F9', padding: 16, overflowY: 'auto' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 },
  modal:        { background: '#fff', borderRadius: 14, padding: 24, maxWidth: 400, width: '90%', position: 'relative' },
  modalClose:   { position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#94a3b8', cursor: 'pointer' },
  modalTitle:   { fontSize: 20, fontWeight: 600, color: '#0f172a', marginBottom: 8 },
  modalText:    { fontSize: 14, color: '#64748b', lineHeight: 1.6 },
  modalInput:   { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 10, outline: 'none', boxSizing: 'border-box' },
  modalBtn:     { width: '100%', padding: '10px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' },
  shareBtn:     { marginTop: 14, padding: '9px 14px', background: '#fff', color: '#185FA5', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  footer:       { textAlign: 'center', padding: '20px 16px', borderTop: '1px solid #e5e7eb', marginTop: 24 },
  footerTagline: { fontSize: 12.5, color: '#64748b', fontStyle: 'italic', marginBottom: 8 },
  footerLinks:  { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' },
  footerDivider: { fontSize: 12, color: '#cbd5e1' },
  footerLink:   { fontSize: 12, color: '#94a3b8', textDecoration: 'none' },
  about:        { marginTop: 32 },
  aboutBanner:  { width: '100%', height: 280, backgroundSize: 'cover', backgroundPosition: 'center 55%', backgroundRepeat: 'no-repeat' },
  aboutCopy:    { background: '#0C447C' },
  aboutInner:   { maxWidth: 700, margin: '0 auto', padding: '48px 24px' },
  aboutTitle:   { fontSize: 28, fontWeight: 700, color: '#fff', marginBottom: 16 },
  aboutText:    { fontSize: 16, lineHeight: 1.7, color: '#DCE9F7', marginBottom: 16 },
  whyThis:      { background: '#fff', borderTop: '1px solid #e5e7eb' },
  whyThisInner: { maxWidth: 900, margin: '0 auto', padding: '56px 24px' },
  whyThisTitle: { fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 16, textAlign: 'center' },
  whyThisText:  { fontSize: 15.5, lineHeight: 1.7, color: '#475569', marginBottom: 14, maxWidth: 680, marginLeft: 'auto', marginRight: 'auto' },
  whyThisSteps: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28, marginTop: 36 },
  whyThisStep:  { textAlign: 'center' },
  whyThisStepIcon: { width: 52, height: 52, borderRadius: '50%', background: '#EAF2FB', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' },
  whyThisStepTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  whyThisStepText:  { fontSize: 13, lineHeight: 1.6, color: '#64748b' },
  whyPro:       { background: '#F4F6F9', borderTop: '1px solid #e5e7eb' },
  whyProInner:  { maxWidth: 820, margin: '0 auto', padding: '56px 24px', textAlign: 'center' },
  whyProTitle:  { fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 8 },
  whyProSubtitle: { fontSize: 15, color: '#64748b', marginBottom: 32, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' },
  whyProGrid:   { display: 'grid', gap: 16, textAlign: 'left', marginBottom: 36 },
  whyProCard:   { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 },
  whyProCardIcon: { fontSize: 22, marginBottom: 8 },
  whyProCardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  whyProCardText:  { fontSize: 13, lineHeight: 1.6, color: '#64748b' },
  whyProPricing: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 20, flexWrap: 'wrap' },
  whyProPrice:   { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  whyProPriceNum: { fontSize: 32, fontWeight: 700, color: '#185FA5', fontFamily: 'monospace' },
  whyProPriceUnit: { fontSize: 13, color: '#64748b', marginTop: -2 },
  whyProPriceNote: { fontSize: 11.5, color: '#94a3b8', marginTop: 4 },
  whyProPriceDivider: { fontSize: 13, color: '#cbd5e1' },
  whyProCta:    { background: '#185FA5', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 32px', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
};

export default App;
